/**
 * The AI tools' refusal for length (400 text_too_long), said about the box
 * it concerns.
 *
 * The five routes fence every text they take, and the server's `field`
 * says which one was too long. A wire field reaches a screen only through
 * this table's label (the clientCreateRefusals.ts shape). The sentences are
 * the owner's: a short box says how many characters to remove,
 * "Who is this for?" says only names go there, a pasted document is run in
 * halves, and a text the refine carries from the original draft, which the
 * person cannot change where they stand, says to draft again.
 *
 * A site keeps what the refusal said (the row, the server's number and the
 * length it sent) and builds the sentence at render, so a refusal that
 * arrives before the tenant config has loaded says the configured ceiling
 * once it does.
 */

import type { AiInputTool } from '@/lib/aiInputLimits';
import { fieldOf, isTextTooLongRefusal, serverMaxCharsOf, type KeyedError } from '@/lib/errorCopy';

/** A number as the sentence says it: English thousands separators. */
export function formatMaxChars(maxChars: number): string {
  return new Intl.NumberFormat('en-US').format(maxChars);
}

export type AiTextBoxKind = 'shorten' | 'split' | 'recipient' | 'carried';
export type AiTextSurface =
  | 'email_generate'
  | 'email_refine'
  | 'assessment_refine'
  | 'brand_format_generate'
  | 'med_list_generate';

export interface AiTextBox {
  /** The label the box shows on screen. */
  label: string;
  kind: AiTextBoxKind;
  /** The config entry this text is fenced against (readAiInputMaxChars). */
  entry: AiInputTool;
}

/** Every text the five routes fence, by surface and by the server's `field`.
 *  The labels are the forms' own `<label>` text (the title without its
 *  "(optional)"); `current_body` is the editor, which has no `<label>`, and
 *  its label is never said (shorten names no box). */
export const AI_TEXT_BOXES: Readonly<Record<AiTextSurface, Readonly<Record<string, AiTextBox>>>> = {
  email_generate: {
    recipient: { label: 'Who is this for?', kind: 'recipient', entry: 'recipient' },
    topic: { label: 'Email Content', kind: 'shorten', entry: 'email' },
    constraints: { label: 'Special instructions', kind: 'shorten', entry: 'email' },
    // Polish takes a pasted email whole; it fixes words, it does not shorten them.
    text: { label: 'Your email', kind: 'split', entry: 'email' },
  },
  email_refine: {
    instruction: { label: 'Want changes?', kind: 'shorten', entry: 'email' },
    // The editor is on screen and editable: a person shortens an email.
    current_body: { label: 'Email draft body', kind: 'shorten', entry: 'email' },
    // The refine sends the brief as it was at Generate, so shortening the
    // box on screen changes nothing it carries: only drafting again does.
    recipient: { label: 'Who is this for?', kind: 'carried', entry: 'recipient' },
    topic: { label: 'Email Content', kind: 'carried', entry: 'email' },
    constraints: { label: 'Special instructions', kind: 'carried', entry: 'email' },
  },
  assessment_refine: {
    instruction: { label: 'Want changes?', kind: 'shorten', entry: 'email' },
    'form_input.notes': { label: 'Notes, observations, or transcript', kind: 'carried', entry: 'assessment_notes' },
  },
  brand_format_generate: {
    source_text: { label: 'Paste your text', kind: 'split', entry: 'brand_formatter' },
    title: { label: 'Document title', kind: 'shorten', entry: 'brand_formatter' },
  },
  med_list_generate: {
    source_text: { label: 'Paste a medication list', kind: 'split', entry: 'med_list' },
  },
};

/** The sentence a box past its ceiling reads, before any call or after a
 *  refusal: its key and vars. `length` is the trimmed text's, counted as
 *  the browser counts it. A short box says how many characters to remove,
 *  and reads the uncounted sentence when that number is not there to say
 *  (no length, or one within the ceiling). */
export function textTooLongCopyForBox(
  box: Pick<AiTextBox, 'kind' | 'label'>,
  maxChars: number,
  length: number | null,
): KeyedError {
  const max_chars = formatMaxChars(maxChars);
  if (box.kind === 'carried') return { key: 'text_too_long_carried', vars: { max_chars, box: box.label } };
  if (box.kind === 'split') return { key: 'text_too_long', vars: { max_chars } };
  if (box.kind === 'recipient') return { key: 'text_too_long_recipient', vars: { max_chars } };
  if (length === null || length <= maxChars) return { key: 'text_too_long_shorten_uncounted', vars: { max_chars } };
  return { key: 'text_too_long_shorten', vars: { max_chars, over_chars: formatMaxChars(length - maxChars) } };
}

/** What a refusal for length said: the box it concerns, the server's
 *  number (null when the body carries no usable one), and the trimmed
 *  length of the text the request sent for that box (null when the
 *  request carries no text there). */
export interface TextTooLongRefusal {
  box: AiTextBox;
  serverMaxChars: number | null;
  sentLength: number | null;
}

/** The text a request sent under a field, read down a dotted path
 *  (`form_input.notes`) over own keys only; null when there is none. */
function sentTextOf(sent: unknown, field: string): string | null {
  let at: unknown = sent;
  for (const part of field.split('.')) {
    if (at === null || typeof at !== 'object' || !Object.hasOwn(at, part)) return null;
    at = (at as Record<string, unknown>)[part];
  }
  return typeof at === 'string' ? at : null;
}

/** The box a 400 text_too_long concerns on this surface, the server's
 *  number and the length sent. Null for any other failure.
 *  `fallbackField` is the box the notice sits under (or the only one the
 *  call can be refused for), read when the body names no box or one this
 *  surface does not have. `sent` is the request body the call sent. */
export function textTooLongRefusalOf(
  e: unknown,
  surface: AiTextSurface,
  fallbackField: string,
  sent: unknown,
): TextTooLongRefusal | null {
  if (!isTextTooLongRefusal(e)) return null;
  const boxes = AI_TEXT_BOXES[surface];
  const field = fieldOf(e);
  // Own keys only: a plain index would read `constructor` or `__proto__`.
  const key = field !== null && Object.hasOwn(boxes, field) ? field : fallbackField;
  const box = Object.hasOwn(boxes, key) ? boxes[key] : undefined;
  if (box === undefined) return null;
  const text = sentTextOf(sent, key);
  return { box, serverMaxChars: serverMaxCharsOf(e), sentLength: text === null ? null : text.trim().length };
}

/** The sentence a kept refusal reads: its key and vars. The number is the
 *  server's, else the box's own ceiling as the site reads it now (`maxOf`). */
export function textTooLongCopyFrom(r: TextTooLongRefusal, maxOf: (entry: AiInputTool) => number): KeyedError {
  return textTooLongCopyForBox(r.box, r.serverMaxChars ?? maxOf(r.box.entry), r.sentLength);
}

/** The sentence a 400 text_too_long reads on this surface: its key and vars.
 *  Null for any other failure. `fallbackField` is the box the notice sits
 *  under (or the only one the call can be refused for), used when the body
 *  names no box or an unknown one. `sent` is the request body. `maxOf` is
 *  the site's reading of a config entry, said only when the body carries
 *  no usable `max_chars`. */
export function textTooLongCopyOf(
  e: unknown,
  surface: AiTextSurface,
  fallbackField: string,
  sent: unknown,
  maxOf: (entry: AiInputTool) => number,
): KeyedError | null {
  const r = textTooLongRefusalOf(e, surface, fallbackField, sent);
  return r === null ? null : textTooLongCopyFrom(r, maxOf);
}

/** What an AI tool site keeps from a failed call: a refusal for length,
 *  resolved to its box, or the registry key of any other failure. */
export type AiToolFailure = { tooLong: TextTooLongRefusal } | { key: string };

/** The failure to keep: the refusal for length on this surface, else the
 *  key the site's own reader gives (aiGenerateKeyOf, aiRefineKeyOf).
 *  `sent` is the request body the call sent. */
export function aiToolFailureOf(
  e: unknown,
  surface: AiTextSurface,
  fallbackField: string,
  sent: unknown,
  keyOf: (e: unknown) => string,
): AiToolFailure {
  const tooLong = textTooLongRefusalOf(e, surface, fallbackField, sent);
  return tooLong !== null ? { tooLong } : { key: keyOf(e) };
}

/** The key and vars a kept failure renders with, the number read now. */
export function aiToolFailureCopy(f: AiToolFailure, maxOf: (entry: AiInputTool) => number): KeyedError {
  return 'tooLong' in f ? textTooLongCopyFrom(f.tooLong, maxOf) : { key: f.key };
}
